import { computed, type Ref, ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Deal, LocaleCode } from '@repo/types'
import { useAPI } from './api'

import { useCurrency, useAreaUnits } from './units'

// info
import {
  ClockIcon,
  HomeIcon,
  WrenchScrewdriverIcon,
  QueueListIcon,
  CalculatorIcon,
  BuildingOfficeIcon,
  Squares2X2Icon,
  CubeIcon,
} from '@heroicons/vue/24/outline'

export function usePropertyDetails (
  deal: Ref<Deal>,
  currencies: Record<string, number> | undefined,
  locale: Ref<LocaleCode>
) {
  const { t } = useI18n()
  const api = useAPI()

  // units

  const { formatShort: formatPriceShort } = useCurrency(currencies);
  const { convert: convertArea } = useAreaUnits();

  const size = computed(() =>
    deal.value.building.type == 'land' ? deal.value.land?.sqm : deal.value.sqm ??
    deal.value.building.sqm)

  const listedAt = computed(() => {
    const s = deal.value.index.createdAt ?? deal.value.createdAt
    return new Date(s).toLocaleDateString()
  })

  const grossYield = ref<number | undefined>(deal.value.grossYield)

  onMounted(async () => {
    if (!grossYield.value && deal.value.building.loc) {
      try {
        const [lng, lat] = deal.value.building.loc.coordinates
        const { data } = await api.call('estimates', { lat, lng })

        const pricePerSqm = (deal.value.price || 0) / convertArea.value(size.value || 0);
        if (data && data.estimatedRentPerSqm && pricePerSqm > 0) {
          grossYield.value = (data.estimatedRentPerSqm * 12) / pricePerSqm;
        }
      } catch (e) {
        console.error('Failed to fetch estimate', e)
      }
    }
  })

  const infoBlocks = computed(() => [
    { icon: ClockIcon, name: 'listed_at', value: listedAt.value },
    {
      icon: HomeIcon,
      name: 'property_type',
      value: deal.value.building.type ? t('enums.homeType.' + deal.value.building.type) : '',
      capitalize: true
    },
    {
      icon: WrenchScrewdriverIcon,
      name: 'year_built', value: deal.value.building.builtAt && new Date(deal.value.building.builtAt).getFullYear()
    },
    { icon: QueueListIcon, name: 'floor', value: deal.value.floor },
    {
      icon: CalculatorIcon,
      name: (deal.value.price ? 'price' : 'rent') + '_per_size',
      value: formatPriceShort.value((deal.value.price || deal.value.rent || 0) / convertArea.value(size.value || 0))
    },
    {
      icon: CalculatorIcon,
      name: 'gross_yield',
      value: grossYield.value ? `${(grossYield.value * 100).toFixed(2)}%` : t('estimating')
    },
  ].filter(b => b.value))

  const buildingInfoBlocks = computed(() => [
    { icon: BuildingOfficeIcon, name: 'Floors', value: deal.value.building.floorsCount },
    { icon: CubeIcon, name: 'Basement floors', value: deal.value.building.basementFloorsCount },
    { icon: Squares2X2Icon, name: 'Total units', value: deal.value.building.totalUnits },
  ].filter(b => b.value))

  // stations
  const stations = computed(() => {
    const baseStations = deal.value.building.nearestStations?.filter(s => s?.name) || [];
    const localizedStations = deal.value.i18n?.[locale.value]?.building?.nearestStations;

    if (localizedStations) {
        return baseStations.map((station, index) => {
            return {
                ...station,
                name: localizedStations[index]?.name || station.name,
                line: localizedStations[index]?.line || station.line,
            };
        });
    }
    return baseStations;
  });

  return {
    infoBlocks,
    buildingInfoBlocks,
    stations,
  }
}
